import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { OrganizationalStructureDto } from "../types";

interface OrganizationalStructuresTreePanelProps {
    isLoading: boolean;
    nodes: OrganizationalStructureDto[];
    selectedOrganizationalStructureId: number | null;
    onSelectOrganizationalStructure: (id: number) => void;
    getNodeName: (node: OrganizationalStructureDto) => string;
}

const getAllNodeIds = (treeNodes: OrganizationalStructureDto[]): number[] =>
    treeNodes.flatMap((node) => [node.id, ...getAllNodeIds(node.childrens || [])]);

const filterTree = (treeNodes: OrganizationalStructureDto[], search: string, getNodeName: (node: OrganizationalStructureDto) => string): OrganizationalStructureDto[] => {
    if (!search.trim()) return treeNodes;

    const normalized = search.trim().toLowerCase();
    return treeNodes
        .map((node) => {
            const children = filterTree(node.childrens || [], normalized, getNodeName);
            const ownName = getNodeName(node).toLowerCase();
            if (ownName.includes(normalized) || children.length > 0) {
                return { ...node, childrens: children };
            }
            return null;
        })
        .filter((node): node is OrganizationalStructureDto => node !== null);
};

export default function OrganizationalStructuresTreePanel({
    isLoading,
    nodes,
    selectedOrganizationalStructureId,
    onSelectOrganizationalStructure,
    getNodeName,
}: OrganizationalStructuresTreePanelProps) {
    const { t } = useTranslation("organizationalStructure");
    const [searchText, setSearchText] = useState("");
    const [showExpandAll, setShowExpandAll] = useState(true);
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

    useEffect(() => {
        setExpandedIds(new Set());
        setShowExpandAll(true);
    }, [nodes]);

    const visibleTree = useMemo(
        () => filterTree(nodes, searchText, getNodeName),
        [nodes, searchText, getNodeName]
    );

    const handleToggleNode = (id: number) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleToggleExpandAll = () => {
        if (showExpandAll) {
            setExpandedIds(new Set(getAllNodeIds(nodes)));
            setShowExpandAll(false);
        } else {
            setExpandedIds(new Set());
            setShowExpandAll(true);
        }
    };

    const renderTree = (treeNodes: OrganizationalStructureDto[], level = 0): ReactNode =>
        treeNodes.map((node) => {
            const hasChildren = (node.childrens || []).length > 0;
            const isExpanded = expandedIds.has(node.id);
            const isSelected = selectedOrganizationalStructureId === node.id;

            return (
                <div key={node.id}>
                    <div
                        className={`organizational-structure-tree-row ${isSelected ? "selected" : ""}`}
                        style={{ paddingLeft: `${level * 24}px` }}
                        onClick={() => onSelectOrganizationalStructure(node.id)}
                    >
                        <button
                            type="button"
                            className="organizational-structure-tree-expand"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (hasChildren) handleToggleNode(node.id);
                            }}
                        >
                            {hasChildren ? (isExpanded ? "-" : "+") : ""}
                        </button>
                        <span className="organizational-structure-tree-label">{getNodeName(node)}</span>
                    </div>
                    {hasChildren && isExpanded && renderTree(node.childrens, level + 1)}
                </div>
            );
        });

    return (
        <>
            <Form.Control
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder={t("searchPlaceholder")}
            />

            <div className="border p-3 organizational-structure-panel mt-3">
                <div
                    className="organizational-structure-tree-toggle"
                    onClick={handleToggleExpandAll}
                >
                    [{showExpandAll ? "+" : "-"}] {showExpandAll ? t("expandAll") : t("collapseAll")}
                </div>

                {isLoading ? (
                    <div className="organizational-structure-loading">
                        <Spinner animation="border" />
                    </div>
                ) : visibleTree.length === 0 ? (
                    <div className="organizational-structure-no-results">
                        {t("organizationalStructure:noOrganizationalStructureFound")}
                    </div>
                ) : (
                    <div>{renderTree(visibleTree)}</div>
                )}
            </div>
        </>
    );
}
